//@flow
import React from 'react';

import type { CustomEvent, FieldClassesType, FieldsType, InputValue } from 'types/ContactForm7';
import { getFieldType, getOptionsFromFieldValuesProps, getPropsFromOptions } from 'utils/contactForm7Utils';
import { getFileNameFromPath } from 'utils/urlUtils';

import { errorClasses } from './CF7Classes';

type Props = {
  authorizedFileFormatMessage: string,
  errorMessage: string,
  field: FieldsType,
  fieldClassesObject: FieldClassesType,
  hasNoData: boolean,
  name: string,
  value: InputValue,

  onCancelForm: (e: CustomEvent) => void,
  onChange: (e: CustomEvent) => void,
  onBlur: (e: CustomEvent) => void
};

class CF7Field extends React.Component<Props> {
  // !TODO : deport this function into CF7Form ? onChange condition checkbox ?
  onCheckboxChange = (e: CustomEvent) => {
    let { name, value } = e.currentTarget;

    if (Array.isArray(this.props.value)) {
      const nextValue = Array.from(this.props.value);
      const existingIndex = nextValue.indexOf(name);

      if (existingIndex !== -1) {
        nextValue.splice(existingIndex, 1);
      } else {
        nextValue.push(name);
      }

      value = nextValue;
    } else {
      value = value ? name : '';
    }

    this.props.onChange({
      currentTarget: { name: this.props.name, value },
      preventDefault: e.preventDefault
    });
  };

  render() {
    const {
      authorizedFileFormatMessage,
      errorMessage,
      field,
      fieldClassesObject,
      hasNoData,
      onBlur,
      onCancelForm,
      onChange,
      value
    } = this.props;

    switch (field.type) {
      case 'email':
      case 'email*':
      case 'text':
      case 'text*':
      case 'number':
      case 'number*':
      case 'date':
      case 'date*':
        const isRequired = field.type.endsWith('*');
        const fieldType = getFieldType(field.type);

        return (
          <>
            <div className={`${fieldClassesObject.fieldContainerDiv} ${errorMessage ? errorClasses.containerDiv : ''}`}>
              <input
                required={isRequired}
                type={fieldType}
                name={field.name}
                // {...getPropsFromOptions(field)} // que le placeholder est pose souci pour le css
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                className={`${fieldClassesObject.input} ${value ? '' : 'is-empty'}`}
              />

              {errorMessage && errorClasses.icon && <i className={`icon ${errorClasses.icon}`} />}

              {field.labels.map(label => (
                <label key={label} htmlFor={field.name} className={`${fieldClassesObject.label}`} data-label={label}>
                  {label} {isRequired ? <abbr title="Ce champ est obligatoire"></abbr> : ''}
                </label>
              ))}
            </div>

            {errorMessage && <span className={errorClasses.span}>{errorMessage}</span>}
          </>
        );

      case 'checkbox':
      case 'checkbox*':
        return (
          <>
            <div
              className={`${fieldClassesObject.containerDiv ?? ''} ${errorMessage ? errorClasses.containerDiv : ''}`}
            >
              {field.labels.map((label, index) => {
                return (
                  // each checkbox
                  <div
                    key={label}
                    className={`${fieldClassesObject.fieldContainerDiv} ${
                      errorMessage ? errorClasses.containerDiv : ''
                    }`}
                  >
                    <input
                      name={label}
                      type="checkbox"
                      key={label}
                      {...getPropsFromOptions(field)}
                      checked={Array.isArray(value) ? value.indexOf(label) !== -1 : value === label}
                      value={Array.isArray(value) ? value.indexOf(label) !== -1 : value === label}
                      onChange={this.onCheckboxChange}
                      className={`${fieldClassesObject.input}`}
                    />

                    <label htmlFor="checkbox" className={`${fieldClassesObject.label}`}>
                      {label}
                    </label>
                  </div>
                );
              })}

              {errorMessage && errorClasses.icon && <i className={`icon ${errorClasses.icon}`} />}
            </div>
            {errorMessage && <span className={errorClasses.span}>{errorMessage}</span>}
          </>
        );

      case 'file':
      case 'file*':
        return (
          <>
            <div className={`${fieldClassesObject.fieldContainerDiv} ${errorMessage ? errorClasses.containerDiv : ''}`}>
              <i className="icon icon-pj"></i>

              <label htmlFor={field.name} className={`${fieldClassesObject.label}`}>
                {!value ? (
                  <>
                    <span>
                      Ajouter un{' '}
                      {field.labels && field.labels.length && field.labels[0].includes('second ') ? 'second ' : ''}
                      fichier **
                    </span>{' '}
                    ou glissez-le ici
                  </>
                ) : (
                  <span>{typeof value === 'string' && getFileNameFromPath(value)}</span>
                )}
              </label>

              <input
                type="file"
                required={field.type.endsWith('*')}
                name={field.name}
                {...getPropsFromOptions(field)}
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                className={`${fieldClassesObject.input}`}
              />
            </div>

            {errorMessage && <span className={errorClasses.span}>{errorMessage}</span>}
          </>
        );

      case 'captchac':
      case 'captchar':
        return <input></input>;

      case 'quiz':
        return <input></input>;

      case 'acceptance':
        return <input></input>;

      case 'hidden':
        return <input type="hidden" {...getPropsFromOptions(field)} name={field.name} />;

      case 'select':
      case 'select*':
        let options = getOptionsFromFieldValuesProps(field);
        const defaultValue = value
          ? value
          : !value && field.options.includes('include_blank') && options
          ? options[0].label
          : '';

        return (
          <>
            <div
              className={`${fieldClassesObject.containerDiv ?? ''} ${errorMessage ? errorClasses.containerDiv : ''}`}
            >
              <div
                className={`${fieldClassesObject.fieldContainerDiv} ${errorMessage ? errorClasses.containerDiv : ''}`}
              >
                <select
                  {...getPropsFromOptions(field)}
                  name={field.name}
                  value={defaultValue}
                  onChange={onChange}
                  onBlur={onBlur}
                  required={field.type.endsWith('*')}
                >
                  {options &&
                    options.map((option, i) => {
                      return (
                        <option
                          key={option.label}
                          value={i === 0 && field.options.includes('include_blank') ? '' : option.label}
                        >
                          {option.label}
                        </option>
                      );
                    })}
                </select>
              </div>

              {errorMessage && <span className={errorClasses.span}>{errorMessage}</span>}
            </div>
          </>
        );

      case 'submit':
        // ! TODO a custom submit (like les comités) or a Submit by default (like clubs)
        return (
          <>
            <div>
              <p className={`${fieldClassesObject.p ?? ''}`}>* champs obligatoires</p>
              <p className={`${fieldClassesObject.p ?? ''}`}>
                {`** taille max : 5 Mo ${authorizedFileFormatMessage ? ` - ${authorizedFileFormatMessage}` : ''}`}
              </p>
            </div>

            <div>
              {!hasNoData && (
                <button className={`${fieldClassesObject.cancelButton ?? ''}`} type="reset" onClick={onCancelForm}>
                  Annuler
                </button>
              )}

              <button className={`${fieldClassesObject.label}`} type={field.type}>
                {field.values}
              </button>
            </div>
          </>
        );

      case 'textarea':
      case 'textarea*':
        return (
          <>
            <div className={`${fieldClassesObject.fieldContainerDiv} ${errorMessage ? errorClasses.containerDiv : ''}`}>
              <textarea
                required={field.type.endsWith('*')}
                name={field.name}
                {...getPropsFromOptions(field)}
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                className={`${fieldClassesObject.input}`}
              />

              {field.labels.map(label => (
                <label key={label} htmlFor={field.name} className={`${fieldClassesObject.label}`}>
                  {label} {field.type.endsWith('*') ? <abbr title="Ce champ est obligatoire"></abbr> : ''}
                </label>
              ))}

              {errorMessage && errorClasses.icon && <i className={`icon ${errorClasses.icon}`} />}
            </div>

            {errorMessage && <span className={errorClasses.span}>{errorMessage}</span>}
          </>
        );
      default:
        break;
    }

    return null;
  }
}

export default CF7Field;
